import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  BellRing,
  BookOpen,
  CheckCheck,
  CreditCard,
  FileText,
  Gift,
  Inbox,
  LayoutTemplate,
  Sparkles,
  X,
} from "lucide-react";
import {
  dismissNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
  type NotificationItem,
  type NotificationType,
} from "../services/notificationsApi";
import { ApiError, isUnrecoverableApiError } from "../services/apiClient";

/** How often the feed is refreshed while the app is open. */
const POLL_INTERVAL_MS = 45_000;

/**
 * Consecutive transient failures (network blips, 5xx) tolerated before the
 * poll gives up for this session. A tab left open on a broken backend should
 * go quiet rather than keep a request every 45s running all day.
 */
const MAX_TRANSIENT_FAILURES = 5;

const TYPE_STYLE: Record<
  NotificationType,
  { icon: typeof Bell; tone: string; label: string }
> = {
  blog: { icon: BookOpen, tone: "bg-sky-100 text-sky-600", label: "Blog" },
  offer: { icon: Gift, tone: "bg-rose-100 text-rose-600", label: "Offer" },
  template: {
    icon: LayoutTemplate,
    tone: "bg-violet-100 text-violet-600",
    label: "New design",
  },
  feature: {
    icon: Sparkles,
    tone: "bg-amber-100 text-amber-600",
    label: "New feature",
  },
  billing: {
    icon: CreditCard,
    tone: "bg-emerald-100 text-emerald-600",
    label: "Billing",
  },
  cv: { icon: FileText, tone: "bg-indigo-100 text-indigo-600", label: "Your CV" },
  system: { icon: Bell, tone: "bg-gray-100 text-gray-600", label: "ResumeX" },
};

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
};

const isExternal = (link: string) => /^https?:\/\//i.test(link);

/**
 * Bell + dropdown feed for the app header.
 *
 * Polls the server every 45s (and on open / tab focus), so anything the
 * backend produces — a new article, a live offer, a purchase receipt, an ATS
 * score — shows up without a reload. New arrivals slide in and the bell
 * rings once, rather than the list silently changing under the reader.
 */
export default function NotificationsPanel() {
  const location = useLocation();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());

  const rootRef = useRef<HTMLDivElement>(null);
  const knownIdsRef = useRef<Set<string> | null>(null);
  const ringTimerRef = useRef<number | null>(null);

  const isMountedRef = useRef(true);
  const pollTimerRef = useRef<number | null>(null);
  /** A request is in flight; a second one would just queue up behind it. */
  const inFlightRef = useRef(false);
  /** Tripped once the feed is known to be unreachable — no further requests. */
  const stoppedRef = useRef(false);
  const failureCountRef = useRef(0);

  /**
   * Give up on the feed for the rest of this session.
   *
   * Clears the interval as well as setting the flag: leaving the timer running
   * and returning early on each tick would still wake the tab 1,900 times a
   * day for nothing.
   */
  const stopPolling = useCallback(() => {
    stoppedRef.current = true;
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    // Never overlap requests, and never resume once we have given up: both
    // are how a 45s poll turns into a continuous stream of failing calls.
    if (inFlightRef.current || stoppedRef.current) return;
    inFlightRef.current = true;

    try {
      const feed = await getNotifications();
      if (!isMountedRef.current) return;
      failureCountRef.current = 0;
      const incoming = new Set(feed.items.map((item) => item.id));

      // Anything not seen on a previous poll is "fresh": highlight it and
      // ring the bell. The very first load seeds the set silently.
      if (knownIdsRef.current) {
        const fresh = feed.items
          .filter((item) => !knownIdsRef.current!.has(item.id) && !item.read)
          .map((item) => item.id);
        if (fresh.length > 0) {
          setFreshIds(new Set(fresh));
          setRinging(true);
          if (ringTimerRef.current) window.clearTimeout(ringTimerRef.current);
          ringTimerRef.current = window.setTimeout(
            () => setRinging(false),
            1400,
          );
        }
      }
      knownIdsRef.current = incoming;

      setItems(feed.items);
      setUnreadCount(feed.unreadCount);
      setError(null);
    } catch (err) {
      if (!isMountedRef.current) return;

      // 401 / 403 / 404 will answer exactly the same way on the next tick.
      // Retrying them is what produced the endless run of failing calls, so
      // stop instead: a fresh session re-mounts this panel and resets it.
      if (isUnrecoverableApiError(err)) {
        stopPolling();
        setError(
          err instanceof ApiError && err.status === 404
            ? "Notifications are unavailable right now."
            : "Sign in again to see your notifications.",
        );
        return;
      }

      // Transient (offline, 5xx): allow a few retries, then go quiet.
      failureCountRef.current += 1;
      if (failureCountRef.current >= MAX_TRANSIENT_FAILURES) stopPolling();
      setError(
        err instanceof Error ? err.message : "Couldn't load notifications.",
      );
    } finally {
      inFlightRef.current = false;
      if (isMountedRef.current) setIsLoading(false);
    }
  }, [stopPolling]);

  // Initial load + polling + refresh when the tab regains focus.
  useEffect(() => {
    isMountedRef.current = true;
    stoppedRef.current = false;
    failureCountRef.current = 0;

    setIsLoading(true);
    void load();
    pollTimerRef.current = window.setInterval(
      () => void load(),
      POLL_INTERVAL_MS,
    );

    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      isMountedRef.current = false;
      inFlightRef.current = false;
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      document.removeEventListener("visibilitychange", onVisible);
      if (ringTimerRef.current) window.clearTimeout(ringTimerRef.current);
    };
  }, [load]);

  // Close on route change and on outside click / Escape.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const toggle = () => {
    setIsOpen((open) => {
      if (!open) void load();
      return !open;
    });
  };

  const markRead = async (item: NotificationItem) => {
    if (item.read) return;
    setItems((prev) =>
      prev.map((entry) =>
        entry.id === item.id ? { ...entry, read: true } : entry,
      ),
    );
    setUnreadCount((count) => Math.max(0, count - 1));
    try {
      await markNotificationsRead([item.id]);
    } catch {
      // The next poll re-syncs; nothing to surface for a read receipt.
    }
  };

  const markAll = async () => {
    setItems((prev) => prev.map((entry) => ({ ...entry, read: true })));
    setUnreadCount(0);
    setFreshIds(new Set());
    try {
      await markAllNotificationsRead();
    } catch {
      void load();
    }
  };

  const dismiss = async (id: string) => {
    const target = items.find((entry) => entry.id === id);
    setItems((prev) => prev.filter((entry) => entry.id !== id));
    if (target && !target.read) {
      setUnreadCount((count) => Math.max(0, count - 1));
    }
    try {
      await dismissNotification(id);
    } catch {
      void load();
    }
  };

  const badge = useMemo(
    () => (unreadCount > 9 ? "9+" : unreadCount > 0 ? String(unreadCount) : ""),
    [unreadCount],
  );

  return (
    <div className="relative" ref={rootRef}>
      <motion.button
        type="button"
        onClick={toggle}
        className="relative rounded-lg p-2 text-gray-600 transition-all hover:bg-gray-100"
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        aria-expanded={isOpen}
        animate={
          ringing
            ? { rotate: [0, -18, 16, -12, 10, -6, 4, 0] }
            : { rotate: 0 }
        }
        transition={{ duration: 1.1, ease: "easeInOut" }}
      >
        {ringing || unreadCount > 0 ? (
          <BellRing className="h-5 w-5" />
        ) : (
          <Bell className="h-5 w-5" />
        )}
        <AnimatePresence>
          {badge && (
            <motion.span
              key={badge}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.4, opacity: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 22 }}
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white"
            >
              {badge}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="fixed left-2 right-2 top-16 z-50 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[22rem]"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Notifications
                </p>
                <p className="text-xs text-gray-500">
                  {unreadCount > 0
                    ? `${unreadCount} unread`
                    : "You're all caught up"}
                </p>
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAll()}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 transition hover:bg-indigo-50"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-[60vh] overflow-y-auto sm:max-h-96">
              {error && (
                <div className="px-4 py-3 text-xs text-red-600">{error}</div>
              )}

              {!error && isLoading && items.length === 0 && (
                <div className="space-y-3 p-4">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="flex animate-pulse gap-3">
                      <div className="h-9 w-9 rounded-xl bg-gray-100" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-3/4 rounded bg-gray-100" />
                        <div className="h-3 w-1/2 rounded bg-gray-100" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!error && !isLoading && items.length === 0 && (
                <div className="flex flex-col items-center px-4 py-10 text-center">
                  <Inbox className="h-8 w-8 text-gray-300" />
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    Nothing here yet
                  </p>
                  <p className="text-xs text-gray-400">
                    New articles, designs, offers and account updates will
                    show up here.
                  </p>
                </div>
              )}

              <AnimatePresence initial={false}>
                {items.map((item) => {
                  const style = TYPE_STYLE[item.type] ?? TYPE_STYLE.system;
                  const Icon = style.icon;
                  const isFresh = freshIds.has(item.id);
                  const content = (
                    <>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${style.tone}`}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span
                            className={`line-clamp-2 text-sm ${
                              item.read
                                ? "font-medium text-gray-700"
                                : "font-semibold text-gray-900"
                            }`}
                          >
                            {item.title}
                          </span>
                          {!item.read && (
                            <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                          )}
                        </span>
                        {item.body && (
                          <span className="mt-0.5 line-clamp-2 block text-xs text-gray-500">
                            {item.body}
                          </span>
                        )}
                        <span className="mt-1 flex items-center gap-2 text-[11px] text-gray-400">
                          <span className="font-medium text-gray-500">
                            {style.label}
                          </span>
                          <span aria-hidden>·</span>
                          <span>{formatRelative(item.createdAt)}</span>
                        </span>
                      </span>
                    </>
                  );
                  const rowClass = `group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                    item.read ? "bg-white hover:bg-gray-50" : "bg-indigo-50/40 hover:bg-indigo-50"
                  }`;

                  return (
                    <motion.div
                      key={item.id}
                      layout
                      initial={
                        isFresh ? { opacity: 0, x: 24, height: 0 } : false
                      }
                      animate={{ opacity: 1, x: 0, height: "auto" }}
                      exit={{ opacity: 0, x: 40, height: 0 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                      className="relative border-b border-gray-100 last:border-0"
                    >
                      {isFresh && (
                        <motion.span
                          initial={{ opacity: 0.6 }}
                          animate={{ opacity: 0 }}
                          transition={{ duration: 2.4 }}
                          className="pointer-events-none absolute inset-0 bg-indigo-100"
                        />
                      )}
                      {item.link && !isExternal(item.link) ? (
                        <Link
                          to={item.link}
                          className={rowClass}
                          onClick={() => {
                            void markRead(item);
                            setIsOpen(false);
                          }}
                        >
                          {content}
                        </Link>
                      ) : item.link ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={rowClass}
                          onClick={() => void markRead(item)}
                        >
                          {content}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={rowClass}
                          onClick={() => void markRead(item)}
                        >
                          {content}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void dismiss(item.id);
                        }}
                        aria-label="Dismiss notification"
                        className="absolute right-2 top-2 rounded-md p-1 text-gray-300 opacity-0 transition hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100 focus:opacity-100"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-center">
              <Link
                to="/blog"
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                onClick={() => setIsOpen(false)}
              >
                Browse the blog {"->"}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
